import React from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Flex, Heading, Text } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import {
  PageSectionId,
  useSectionRef,
} from '@rover/react-lib/src/pages/landing/SeoLocation/hooks/useSectionRef';
import { ValuePropFilterOptionType } from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { getValuePropFilters } from './filters';
import { getTileConfig } from './getTileConfig';
import SquareIconButton from './SquareIconButton';

type ValuePropFilterOptionsProps = {
  locationName: string;
  locale: Locale;
  searchFilters: Partial<SearchFilters>;
  internalServiceTypeSlug: string;
  petType?: PetTypeEnum | null;
  serviceTypeName: string;
  onPress?: (valuePropFilterOption: ValuePropFilterOptionType) => void;
} & Pick<React.ComponentProps<typeof Section>, 'my' | 'mt' | 'mb'>;

const ValuePropFilterOptions: React.FC<ValuePropFilterOptionsProps> = ({
  locationName,
  locale,
  searchFilters,
  internalServiceTypeSlug,
  petType,
  serviceTypeName,
  onPress = () => {},
  ...marginProps
}) => {
  const { i18n } = useLingui();
  const sectionRef = useSectionRef(PageSectionId.ValuePropFilterOptions, { threshold: 1.0 });

  const filters = getValuePropFilters(internalServiceTypeSlug, petType);

  if (filters.length === 0) {
    return null;
  }

  return (
    <Section
      htmlRef={sectionRef}
      contentWidth="full"
      paddingLeft="4x"
      paddingRight="4x"
      maxWidth={['720px', '940px', '1140px']}
      {...marginProps}
    >
      <Heading size="400" mb="3x">
        <Trans>
          Popular requests for {serviceTypeName} - {locationName}
        </Trans>
      </Heading>
      <Text>
        <Trans>Select a filter and find qualifying sitters offering {serviceTypeName}.</Trans>
      </Text>
      <Flex
        mt="6x"
        alignContent="stretch"
        flexDirection={['column', 'row']}
        flexWrap="wrap"
        gap="4x"
      >
        {filters.map((filter) => {
          const { icon, title, searchFilterOverrides, analyticsOption } = getTileConfig(filter);
          return (
            <SquareIconButton
              flex="0 1 calc(50% - 8px)"
              key={filter}
              icon={icon}
              title={i18n._(title)}
              href={getSearchUrl(
                { ...searchFilters, ...searchFilterOverrides, bypassInterstitial: true },
                locale
              )}
              onPress={() => {
                onPress(analyticsOption);
              }}
            />
          );
        })}
      </Flex>
    </Section>
  );
};

export default ValuePropFilterOptions;
